import moment from "moment";
import React, { useMemo, useState } from "react";
import Button1 from "../../form/Button1";
import { useSelector } from "react-redux";
import { buildComparePassengers, ComparePAX } from "./ComparePAX";
import { PriceBreakdown } from "./PriceBreakdown";
import { enqueueSnackbar } from "notistack";
import StaffApiErrorModal, {
  getStaffApiErrorCopy,
} from "../StaffApiErrorModal";
import setChangeFee from "../../../controllers/booking/postTicketing/change/setChangeFee";
import ManualChange from "./ManualChange";
// import selectFlightChange from "../../../controllers/booking/postTicketing/change/selectFlightChange";
import setSelectedSegment from "../../../controllers/booking/postTicketing/change/setSelectedSegment";
import { Modal3 } from "../../DIsplay/Modal/Modal3";
import ExpiryInput from "./ExpiryInput";
import { applicableFee } from "../../../utils/booking/changeFee";
import { formatMoney } from "../../../features/utils/formatMoney";
import { defaultMarkupTargets } from "./PassengerBreakdownCard";

import ChangeSelectedOfferHint from "../../../pages/TicketManagement/Flight/Change/ChangeSelectedOfferHint";
import RelatedDocsPanel, {
  normalizeRelatedDocs,
} from "./RelatedDocsPanel";

export default function ManualChangeQuote({
  data,
  reload,
  close,
  onViewSelectedOffer,
}) {
  const [details, setDetails] = useState(data?.remark || "");
  const [loadings, setLoadings] = useState({
    submit: false,
  });
  const [apiError, setApiError] = useState(null);

  // NOTE: PriceBreakdown will callback with array rows
  const [feeData, setFeeData] = useState([
    {
      passengerId: 1,
      type: "Adt",
      ticketDifference: 0,
      airlineFareDifference: 0,
      airlineChangePrice: 0,
      airlinePenaltyFee: 0,
      airlineTaxDifference: 0,
      airlineSupplierFee: 0,
      fareDifferenceMarkup: 0,
      markupVariant: "Markup",
      markupType: "Fixed",
      markupValue: 0,
      changePrice: 0,
      penaltyFee: 0,
      taxDifference: 0,
      supplierFee: 0,
      edit: false,
    },
  ]);

  const [openConfirm, setOpenConfirm] = useState(false);
  const [paymentDeadline, setPaymentDeadline] = useState(
    moment().add(3, "hours").toISOString(),
  );

  const [travelers] = useState([]);

  const {
    paymentSettings: { fees },
  } = useSelector((state) => state.paymentSettings);

  const flightsFromSelected = (() => {
    if (data?.selectedOffer) {
      try {
        const offer = JSON.parse(data?.selectedOffer);
        if (!offer) return;
        const segments = offer?.directions?.flatMap(
          (direction, directionIndex) =>
            direction?.map((segment) => {
              return {
                ...segment,
                segment: String(directionIndex + 1),
                offerId: offer?.id,
                airline:
                  segment?.airline?.marketing || segment?.airline?.operating,
                airlineImage: segment?.airline?.image?.url,
                airlineName: segment?.airline?.image?.description,
                arrivalAirport: segment?.arrival?.airport,
                arrivalDate: segment?.arrival?.date,
                arrivalLocation: segment?.arrival?.location,
                arrivalLocationName: segment?.arrival?.city,
                arrivalTerminal: segment?.arrival?.terminal,
                arrivalTime: segment?.arrival?.time,
                departureAirport: segment?.departure?.airport,
                departureDate: segment?.departure?.date,
                departureLocation: segment?.departure?.location,
                departureLocationName: segment?.departure?.city,
                departureTerminal: segment?.departure?.terminal,
                departureTime: segment?.departure?.time,
              };
            }),
        );
        return segments;
      } catch (error) {
        return [];
      }
    }
    return [];
  })();

  const selectedSegmentsFromOrder = Array.isArray(data?.selectedSegment)
    ? data.selectedSegment.map((segment, index) => ({
        ...segment,
        segment: segment?.segment || String(index + 1),
        airline: segment?.airline?.marketing || segment?.airline?.operating,
        airlineImage: segment?.airline?.image?.url,
        airlineName: segment?.airline?.image?.description,
        arrivalAirport: segment?.arrivalAirport || segment?.arrival?.airport,
        arrivalDate: segment?.arrivalDate || segment?.arrival?.date,
        arrivalLocation: segment?.arrivalLocation || segment?.arrival?.location,
        arrivalLocationName:
          segment?.arrivalLocationName || segment?.arrival?.city,
        arrivalTerminal: segment?.arrivalTerminal || segment?.arrival?.terminal,
        arrivalTime: segment?.arrivalTime || segment?.arrival?.time,
        departureAirport:
          segment?.departureAirport || segment?.departure?.airport,
        departureDate: segment?.departureDate || segment?.departure?.date,
        departureLocation:
          segment?.departureLocation || segment?.departure?.location,
        departureLocationName:
          segment?.departureLocationName || segment?.departure?.city,
        departureTerminal:
          segment?.departureTerminal || segment?.departure?.terminal,
        departureTime: segment?.departureTime || segment?.departure?.time,
      }))
    : [];

  const flights = selectedSegmentsFromOrder?.length
    ? selectedSegmentsFromOrder
    : data?.selectedOffer
      ? flightsFromSelected
      : data?.flightBooking
          ?.at(0)
          ?.flights?.filter((flight) =>
            data?.directionsToChange?.includes(flight?.segmentRef),
          )
          ?.map((obj, i) => {
            const newSeg = data?.newSegment?.find(
              (ob) => ob?.segmentRef === obj?.segmentRef,
            );
            return {
              ...obj,
              departureDate: moment(
                newSeg?.departure?.date ||
                  data?.newDirections?.at(Number(obj?.segment) - 1)?.departure,
              )?.format("YYYY-MM-DD"),
              arrivalDate: moment(
                newSeg?.arrival?.date ||
                  data?.newDirections?.at(Number(obj?.segment) - 1)?.departure,
              )?.format("YYYY-MM-DD"),
              departureTime: newSeg?.departure?.time || obj?.departureTime,
              arrivalTime: newSeg?.arrival?.time || obj?.arrivalTime,
            };
          }) || [];

  const [newSegment, setNewSegment] = useState({
    flights,
    destinations: data?.flightBooking
      ?.at(0)
      ?.destinations?.filter((_, i) =>
        flights?.find((flight) => Number(flight?.segment) - 1 === i),
      )
      ?.map((obj, i) => {
        return {
          ...obj,
          departureDate: moment(
            flights?.find((flight) => Number(flight?.segment) - 1 === i)
              ?.departureDate,
          )?.format("YYYY-MM-DD"),
        };
      }),
  });

  async function handleManual() {
    const payload = {
      changeOrderId: data?._id,
      ...newSegment,
      travelers: travelers || [],
    };

    const newSegmentObject = payload?.flights?.map((obj) => ({
      ...obj,
      _id: undefined,
      stops: undefined,
      aircraftCode: obj?.aircraftCode,
      flightNumber: obj?.flightNumber,
      departure: {
        time: moment(obj?.departureTime, "hh:mm A").format("HH:mm"),
        date: moment(obj?.departureDate).format("YYYY-MM-DD"),
        location: obj?.departureLocation,
        airport: obj?.departureAirport,
        city: obj?.departureCity,
      },
      arrival: {
        time: moment(obj?.arrivalTime, "hh:mm A").format("HH:mm"),
        date: moment(obj?.arrivalDate).format("YYYY-MM-DD"),
        location: obj?.arrivalLocation,
        airport: obj?.arrivalAirport,
        city: obj?.arrivalCity,
      },
      cabinClass: obj?.cabinClass,
      bookingClass: obj?.bookingClass,
      segmentRef: obj?.segmentRef,
      airline: {
        image: { url: obj?.airlineImage, description: obj?.airlineName },
        marketing: obj?.airline,
        operating: obj?.airline,
      },
      id: data?._id,
    }));

    setLoadings({ ...loadings, save: true });
    const res = await setSelectedSegment({
      changeOrderId: data?._id,
      segments: newSegmentObject,
    });
    setLoadings({ ...loadings, save: false });

    if (res.return) {
      close && close();
      window.location.reload();
      return true;
    }
    setApiError(getStaffApiErrorCopy(res));
    return false;
  }

  async function handleSubmit() {
    const sum = (rows, key) =>
      rows.reduce((acc, row) => acc + Number(row?.[key] || 0), 0);

    // Persist both: airline/original + markup rule + final selling amounts
    const priceBreakdown = (feeData || []).map((obj) => {
      const airlineChangePrice = Number(
        obj?.airlineChangePrice ?? obj?.changePrice ?? 0,
      );
      const airlineFareDifference = Number(
        obj?.airlineFareDifference ?? obj?.ticketDifference ?? 0,
      );
      const airlinePenaltyFee = Number(
        obj?.airlinePenaltyFee ?? obj?.penaltyFee ?? 0,
      );
      const airlineTaxDifference = Number(
        obj?.airlineTaxDifference ?? obj?.taxDifference ?? 0,
      );
      const airlineSupplierFee = Number(
        obj?.airlineSupplierFee ?? obj?.supplierFee ?? 0,
      );
      const changePrice = Number(obj?.changePrice || 0);
      const ticketDifference = Number(obj?.ticketDifference || 0);
      const penaltyFee = Number(obj?.penaltyFee ?? airlinePenaltyFee);
      const taxDifference = Number(obj?.taxDifference ?? airlineTaxDifference);
      const supplierFee = Number(obj?.supplierFee ?? airlineSupplierFee);

      const markupTargets = { ...defaultMarkupTargets };

      return {
        passengerId: (obj?.passengerId || "")?.toString(),
        airlineChangePrice,
        airlineFareDifference,
        airlinePenaltyFee,
        airlineTaxDifference,
        airlineSupplierFee,
        changePrice,
        ticketDifference,
        penaltyFee,
        taxDifference,
        supplierFee,
        fareDifferenceMarkup: Number(
          obj?.fareDifferenceMarkup ?? ticketDifference - airlineFareDifference,
        ),
        changePriceMarkup: Number(
          obj?.changePriceMarkup ?? changePrice - airlineChangePrice,
        ),
        penaltyFeeMarkup: Number(
          obj?.penaltyFeeMarkup ?? penaltyFee - airlinePenaltyFee,
        ),
        taxDifferenceMarkup: Number(
          obj?.taxDifferenceMarkup ?? taxDifference - airlineTaxDifference,
        ),
        supplierFeeMarkup: Number(
          obj?.supplierFeeMarkup ?? supplierFee - airlineSupplierFee,
        ),
        markupVariant: obj?.markupVariant || "Markup",
        markupType: obj?.markupType || "Fixed",
        markupValue: Number(obj?.markupValue || 0),
        markupTargets,
        markupApplyChangeFee: false,
        markupApplyFareDifference: true,
        markupApplyPenalty: false,
        markupApplyTaxDifference: false,
        markupApplySupplierFee: false,
      };
    });

    const linesTotal =
      sum(priceBreakdown, "changePrice") +
      sum(priceBreakdown, "ticketDifference") +
      sum(priceBreakdown, "penaltyFee") +
      sum(priceBreakdown, "taxDifference") +
      sum(priceBreakdown, "supplierFee");
    const feeConfig =
      data?.changeRequest === "PassengerInfo"
        ? fees?.changePassengerDetailFee
        : fees?.reIssueFee;
    const servicePerPax = applicableFee(linesTotal, feeConfig);
    const serviceTotal = servicePerPax * (priceBreakdown.length || 1);

    const reqBody = {
      changeOrderId: data?._id,
      remark: details,
      paymentDeadline,
      // Per-passenger mix is the source of truth — airline + markup + finals all here
      priceBreakdown,
    };

    setLoadings({ ...loadings, submit: true });
    const res = await setChangeFee(reqBody);
    setLoadings({ ...loadings, submit: false });

    if (res.return) {
      try {
        const first = priceBreakdown[0] || {};
        sessionStorage.setItem(
          `changeQuoteAdmin:${data?._id}`,
          JSON.stringify({
            savedAt: Date.now(),
            priceBreakdown,
            airlineChangePrice: sum(priceBreakdown, "airlineChangePrice"),
            airlineFareDifference: sum(
              priceBreakdown,
              "airlineFareDifference",
            ),
            airlineChangePenalty: sum(priceBreakdown, "airlinePenaltyFee"),
            airlineTaxDifference: sum(priceBreakdown, "airlineTaxDifference"),
            airlineSupplierFee: sum(priceBreakdown, "airlineSupplierFee"),
            fareDifferenceMarkup: sum(priceBreakdown, "fareDifferenceMarkup"),
            sellingFareDifference: sum(priceBreakdown, "ticketDifference"),
            taxDifference: sum(priceBreakdown, "taxDifference"),
            supplierFee: sum(priceBreakdown, "supplierFee"),
            markupVariant: first?.markupVariant || "Markup",
            markupType: first?.markupType || "Fixed",
            markupValue: Number(first?.markupValue || 0),
            intraverseServiceFee: serviceTotal,
            currency:
              data?.currency ||
              data?.flightBooking?.[0]?.currency ||
              data?.flightBooking?.currency ||
              null,
          }),
        );
      } catch (_) {
        /* ignore quota / private mode */
      }
      enqueueSnackbar("Quote Complete.", { variant: "success" });
      reload && reload();
    } else setApiError(getStaffApiErrorCopy(res));
  }

  function handlePriceChange(next) {
    setFeeData(next);
  }

  // Zero-balance quotes are valid (no extra collection).
  const canShowBottomActions = useMemo(() => {
    const arr = feeData || [];
    return arr.length > 0;
  }, [feeData]);

  // Total charge + airline cost so ops can see markup & service fee
  const { airlineTotal, markupProfit, serviceTotal, totalChargeAllPassengers } =
    useMemo(() => {
      const empty = {
        airlineTotal: 0,
        markupProfit: 0,
        serviceTotal: 0,
        totalChargeAllPassengers: 0,
      };
      const arr = feeData || [];
      if (!arr.length) return empty;

      const lines = arr.reduce(
        (acc, cur) =>
          acc +
          Number(cur?.changePrice || 0) +
          Number(cur?.penaltyFee || 0) +
          Number(cur?.ticketDifference || 0),
        0,
      );

      const airline = arr.reduce(
        (acc, cur) =>
          acc +
          Number(cur?.airlineChangePrice ?? cur?.changePrice ?? 0) +
          Number(cur?.airlineFareDifference ?? cur?.ticketDifference ?? 0) +
          Number(cur?.airlinePenaltyFee ?? cur?.penaltyFee ?? 0),
        0,
      );

      const fee =
        data?.changeRequest === "PassengerInfo"
          ? fees?.changePassengerDetailFee
          : fees?.reIssueFee;

      const fromSettings = applicableFee(lines, fee) * (arr.length || 1);
      const saved = Number(data?.intraverseServiceFee || data?.changeFee || 0);
      const service = saved > 0 ? saved : fromSettings;

      return {
        airlineTotal: airline,
        markupProfit: lines - airline,
        serviceTotal: service,
        totalChargeAllPassengers: lines + service,
      };
    }, [
      feeData,
      fees,
      data?.changeRequest,
      data?.intraverseServiceFee,
      data?.changeFee,
    ]);

  return (
    <>
    <div className="flex flex-col gap-3 max-h-[80vh] overflow-y-auto scroll-hide">
      {onViewSelectedOffer ? (
        <ChangeSelectedOfferHint
          data={data}
          onView={onViewSelectedOffer}
        />
      ) : null}
      {/* Itinerary / passenger compare */}
      <div className="shrink-0 rounded-xl border border-[#E8EEF5] bg-white">
        {data?.changeRequest === "PassengerInfo" ? (
          <div className="p-3">
            <ComparePAX
              data={{ passengers: buildComparePassengers(data) }}
            />
          </div>
        ) : (
          <div className="p-3">
            <ManualChange
              callback={reload}
              {...{ data, newSegment, setNewSegment, flights }}
            />
          </div>
        )}
      </div>

      {data?.changeRequest === "PassengerInfo" ||
      normalizeRelatedDocs(data).length ? (
        <RelatedDocsPanel
          data={data}
          title="Supporting documents"
          empty={data?.changeRequest === "PassengerInfo"}
        />
      ) : null}

      {/* Quote amounts */}
      <div className="shrink-0 rounded-xl border border-[#E8EEF5] bg-white p-3">
        <div className="flex items-baseline justify-between gap-2 mb-2">
          <h3 className="text-sm font-semibold text-[#0F172A]">Quote</h3>
          <p className="text-[11px] text-[#6D7C94] text-right">
            Final amounts to agent · service fee added by fee config
          </p>
        </div>
        <PriceBreakdown data={data} callback={handlePriceChange} />
      </div>

      {/* Meta */}
      <div className="shrink-0 rounded-xl border border-[#E8EEF5] bg-white p-3 flex flex-col gap-3">
        <ExpiryInput callback={(d) => setPaymentDeadline(d)} />

        <div className="border-t border-[#E8EEF5] pt-2.5">
          <div className="flex items-center justify-between mb-1">
            <label className="text-[10px] uppercase tracking-wide text-[#94A3B8] font-semibold">
              Remarks
            </label>
            <span className="text-[10px] text-[#94A3B8]">Optional</span>
          </div>
          <textarea
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            placeholder="Notes for this quote…"
            rows={2}
            className="w-full min-h-[56px] max-h-28 px-2.5 py-1.5 text-sm border border-[#DBDBDB] rounded-lg text-[#0F172A] placeholder:text-[#A4B1C1] resize-y focus:outline-none focus:border-[#94A3B8]"
          />
        </div>
      </div>

      <Modal3
        open={openConfirm}
        setOpen={() => setOpenConfirm(false)}
        title="Confirm Quote"
      >
        <div className="flex flex-col gap-3 p-4">
          <p className="text-sm text-[#0F172A]">
            {totalChargeAllPassengers <= 0
              ? "No extra amount is due. Confirm so the change can go to the airline without waiting for payment."
              : "Confirm this quote? The agent will only see the adjusted total."}
          </p>
          <div className="rounded-lg bg-[#F8FAFC] border border-[#E8EEF5] px-3 py-2 space-y-1.5">
            <div className="flex justify-between items-center">
              <span className="text-xs text-[#6D7C94]">Airline fees</span>
              <span className="text-xs font-medium text-[#0F172A]">
                {formatMoney(airlineTotal)}
              </span>
            </div>
            <div className="flex justify-between items-center gap-3">
              <span className="text-xs text-[#6D7C94]">
                Markup &amp; service fee
              </span>
              <span
                className={`text-xs font-semibold text-right ${
                  markupProfit + serviceTotal > 0
                    ? "text-emerald-700"
                    : markupProfit + serviceTotal < 0
                      ? "text-amber-700"
                      : "text-[#0F172A]"
                }`}
              >
                Markup {markupProfit > 0 ? "+" : ""}
                {formatMoney(markupProfit)}
                {" · "}
                Service {formatMoney(serviceTotal)}
              </span>
            </div>
            <div className="flex justify-between items-center border-t border-[#E8EEF5] pt-1.5">
              <span className="text-xs text-[#6D7C94]">Total charge</span>
              <span className="text-sm font-semibold text-[#0F172A]">
                {formatMoney(totalChargeAllPassengers)}
              </span>
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={() => setOpenConfirm(false)}
              className="h-8 rounded-lg px-3 border border-[#E2E8F0] text-xs font-semibold text-[#334155] bg-white hover:bg-[#F8FAFC]"
            >
              Cancel
            </button>
            <Button1 loading={loadings?.submit} onClick={handleSubmit}>
              Confirm
            </Button1>
          </div>
        </div>
      </Modal3>

      {canShowBottomActions ? (
        <div className="sticky bottom-0 z-10 border-t border-[#E8EEF5] bg-white px-3 py-2.5">
          <div className="flex items-center justify-between gap-3">
            <div className="flex flex-col leading-tight min-w-0">
              <span className="text-[10px] uppercase tracking-wide text-[#94A3B8] font-semibold">
                Total charge
              </span>
              <span className="text-sm font-semibold text-[#0F172A]">
                {formatMoney(totalChargeAllPassengers)}
              </span>
              <span className="text-[10px] text-[#6D7C94] truncate">
                Airline {formatMoney(airlineTotal)}
                {" · "}
                <span
                  className={
                    markupProfit + serviceTotal > 0
                      ? "text-emerald-700"
                      : markupProfit + serviceTotal < 0
                        ? "text-amber-700"
                        : undefined
                  }
                >
                  Markup {markupProfit > 0 ? "+" : ""}
                  {formatMoney(markupProfit)}
                  {" · "}
                  Service {formatMoney(serviceTotal)}
                </span>
              </span>
            </div>

            <div className="flex items-center justify-end gap-2 shrink-0">
              <button
                type="button"
                onClick={() => close && close()}
                className="h-8 rounded-lg px-3 border border-[#E2E8F0] text-xs font-medium text-[#475569] bg-white hover:bg-[#F8FAFC]"
              >
                Cancel
              </button>

              <button
                type="button"
                disabled={loadings?.save}
                onClick={handleManual}
                className={`h-8 rounded-lg px-3 border text-xs font-semibold
                  ${
                    loadings?.save
                      ? "bg-[#F1F5F9] text-[#94A3B8] border-[#E2E8F0] cursor-not-allowed"
                      : "bg-white text-[#0F172A] border-[#CBD5E1] hover:bg-[#F8FAFC]"
                  }`}
              >
                {loadings?.save ? "Saving..." : "Save"}
              </button>

              <button
                type="button"
                disabled={loadings?.submit}
                onClick={() => setOpenConfirm(true)}
                className={`h-8 rounded-lg px-3.5 text-xs font-semibold text-white
                  ${
                    loadings?.submit
                      ? "bg-[#CBD5E1] cursor-not-allowed"
                      : "bg-primary1 hover:opacity-90"
                  }`}
              >
                Confirm quote
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
    <StaffApiErrorModal
      open={Boolean(apiError)}
      setOpen={(next) => {
        if (!next) setApiError(null);
      }}
      title={apiError?.title}
      text={apiError?.text}
    />
    </>
  );
}
